/**
 * SKRIVNINGARNA — en operation i SYNC_PUSH:s kuvert, byggd ur kontraktets typer.
 *
 * DET FINNS INGEN SKRIVRUTT PER TABELL. Tidraden, milraden, utlägget, frånvaron
 * och veckans tillstånd går alla genom SYNC_PUSH som operationer (shared/types.ts
 * avsnitt 8). Funktionerna nedan bygger dem, och de bygger BARA skrivtyperna —
 * TimeEntryWrite, MileageWrite, ExpenseWrite, AbsenceWrite. Ingen av dem
 * innehåller ett prisfält, en synkkolumn eller ett invoice_run_id, så
 *
 *   BESLUT 15 GÅR INTE ATT BRYTA HÄRIFRÅN: klienten kan inte ens UTTRYCKA ett
 *   timpris. Servern slår upp det i prislistan och FRYSER det på raden
 *   tillsammans med varifrån det kom. Saknas priset får raden PRICE_MISSING och
 *   sparas inte alls — hellre det än en rad utan pris som börjar ändra sig när
 *   prislistan justeras.
 *
 * OP_ID MYNTAS HÄR MEN ÄGS AV ANROPAREN (beslut 09). Varje funktion myntar ETT
 * op_id när den kallas; den som skickar om MÅSTE skicka SAMMA operation igen och
 * inte bygga en ny. Se useSparning i spara.ts, som håller operationerna kvar
 * mellan försöken just av det skälet — ett nytt op_id per försök gör varje
 * omsändning till en ny skrivning.
 *
 * BASE_VERSION: null betyder "raden ska inte finnas" och ger alltid version 1.
 * Ett tal betyder "raden står på exakt den versionen". En RÄTTELSE av en
 * befintlig rad skickar alltså radens nuvarande version, en NY rad skickar null.
 */

import { asMillis } from '@delat/types';
import type {
  AbsenceId,
  AbsenceKind,
  AccountId,
  EntrySource,
  ExpenseId,
  IsoWeek,
  Meters,
  MileageId,
  Minutes,
  Ore,
  ObjectKey,
  SyncOp,
  TaskId,
  TimeEntryId,
  Vehicle,
  Version,
  WeekTransition,
  WorkDate,
  WorkOrderId,
} from '@delat/types';
import { granska, nyUlid } from '../../api';
import {
  franvaroId,
  milradId,
  tidradId,
  utlaggsradId,
} from '../../api/granskare/tidrapport';

const nyttOpId = () => granska.opId(nyUlid(), 'op_id');

/**
 * client_time är OBLIGATORISKT i SyncOp men ingår ALDRIG i request_hash — annars
 * byter en omsändning ur utkorgen hash och får DUPLICATE_OP. Servern stämplar
 * med sin egen klocka.
 */
const nu = () => asMillis(Date.now());

/* --- Nya id ------------------------------------------------------------- */

/**
 * ID GENERERAS PÅ KLIENTEN, alltid (api/id.ts). Det är det som gör att en rad
 * kan skapas och refereras utan att nätet varit inblandat.
 */
export const nyttTidradId = (): TimeEntryId => tidradId(nyUlid(), 'time_entry.id');
export const nyttMilradId = (): MileageId => milradId(nyUlid(), 'mileage.id');
export const nyttUtlaggsradId = (): ExpenseId => utlaggsradId(nyUlid(), 'expense.id');
export const nyttFranvaroId = (): AbsenceId => franvaroId(nyUlid(), 'absence.id');

/* --- Tidraden ----------------------------------------------------------- */

export interface Tidradsfalt {
  readonly id: TimeEntryId;
  readonly account_id: AccountId;
  readonly work_date: WorkDate;
  readonly minutes: Minutes;
  readonly task_id: TaskId;
  readonly work_order_id: WorkOrderId;
  /** null = ingen anteckning. Tom sträng är INTE samma sak (CHECK i schemat). */
  readonly note: string | null;
  /** Beslut 02: 'voice' bara när raden faktiskt kom ur rösten. */
  readonly source: EntrySource;
  readonly base_version: Version | null;
}

export const tidradOp = (falt: Tidradsfalt): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'put',
  table: 'time_entry',
  id: falt.id,
  base_version: falt.base_version,
  row: {
    account_id: falt.account_id,
    work_date: falt.work_date,
    minutes: falt.minutes,
    task_id: falt.task_id,
    work_order_id: falt.work_order_id,
    note: falt.note,
    source: falt.source,
  },
});

/* --- Milraden ----------------------------------------------------------- */

export interface Milradsfalt {
  readonly id: MileageId;
  readonly account_id: AccountId;
  readonly work_date: WorkDate;
  /** Beslut 08: skrivs FÖR HAND. 4,5 mil = 45000 meter. */
  readonly meters: Meters;
  readonly work_order_id: WorkOrderId;
  /** Informationsfält. Påverkar aldrig milbeloppet — satsen är firmans. */
  readonly vehicle: Vehicle;
  readonly base_version: Version | null;
}

export const milradOp = (falt: Milradsfalt): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'put',
  table: 'mileage',
  id: falt.id,
  base_version: falt.base_version,
  row: {
    account_id: falt.account_id,
    work_date: falt.work_date,
    meters: falt.meters,
    work_order_id: falt.work_order_id,
    vehicle: falt.vehicle,
  },
});

/* --- Utlagget ----------------------------------------------------------- */

export interface Utlaggsfalt {
  readonly id: ExpenseId;
  readonly account_id: AccountId;
  readonly work_date: WorkDate;
  /**
   * EX MOMS. Kvittots totalsumma är INKL moms och får aldrig skrivas hit —
   * annars vidarefaktureras varje utlägg 25 % för högt, systematiskt och
   * osynligt (schema/company.sql). Fältet i gränssnittet heter därför
   * "Summa exkl. moms".
   */
  readonly amount_ore: Ore;
  readonly description: string;
  readonly receipt_object_key: ObjectKey | null;
  /** 1 = vidarefaktureras. Då KRÄVER schemat en arbetsorder. */
  readonly billable: 0 | 1;
  readonly work_order_id: WorkOrderId | null;
  readonly base_version: Version | null;
}

export const utlaggOp = (falt: Utlaggsfalt): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'put',
  table: 'expense',
  id: falt.id,
  base_version: falt.base_version,
  row: {
    account_id: falt.account_id,
    work_date: falt.work_date,
    amount_ore: falt.amount_ore,
    description: falt.description,
    receipt_object_key: falt.receipt_object_key,
    billable: falt.billable,
    work_order_id: falt.work_order_id,
  },
});

/* --- Frånvaron ---------------------------------------------------------- */

export const franvaroOp = (falt: {
  readonly id: AbsenceId;
  readonly account_id: AccountId;
  readonly work_date: WorkDate;
  readonly kind: AbsenceKind;
  readonly base_version: Version | null;
}): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'put',
  table: 'absence',
  id: falt.id,
  base_version: falt.base_version,
  row: {
    account_id: falt.account_id,
    work_date: falt.work_date,
    kind: falt.kind,
  },
});

/* --- Raderingar --------------------------------------------------------- */

/**
 * En radering är en MJUK radering och en ny version av raden. base_version är
 * radens NUVARANDE version; stämmer den inte har någon annan hunnit före och
 * svaret blir SYNC_CONFLICT.
 */
export const raderaTidradOp = (id: TimeEntryId, base_version: Version): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'delete',
  table: 'time_entry',
  id,
  base_version,
});

export const raderaMilradOp = (id: MileageId, base_version: Version): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'delete',
  table: 'mileage',
  id,
  base_version,
});

export const raderaUtlaggOp = (id: ExpenseId, base_version: Version): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'delete',
  table: 'expense',
  id,
  base_version,
});

export const raderaFranvaroOp = (id: AbsenceId, base_version: Version): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'delete',
  table: 'absence',
  id,
  base_version,
});

/* --- Veckan ------------------------------------------------------------- */

/**
 * BESLUT 04: veckan är enda periodbegreppet. Övergångarna står som DATA i
 * WEEK_TRANSITIONS (shared/routes.ts) — härled knappen ur den, gissa aldrig.
 * 'submit' är den enda övergång tidrapporten själv gör; approve, lock och reopen
 * hör till attestskärmen och är MANAGER_ONLY.
 */
export const veckoOp = (
  account_id: AccountId,
  iso_week: IsoWeek,
  transition: WeekTransition,
): SyncOp => ({
  op_id: nyttOpId(),
  client_time: nu(),
  kind: 'week',
  account_id,
  iso_week,
  transition,
});
